import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Forecast {
  id: string
  period: string
  baseline: number
  optimistic: number
  conservative: number
  ai_prediction: number
  confidence: number
  variance_to_target: number
}

interface ForecastModel {
  id: string
  name: string
  accuracy: number
  mape: number
  last_prediction: number
  actual_outcome: number
  bias: number
  recommended: boolean
}

interface Scenario {
  id: string
  name: string
  probability: number
  revenue_impact: number
  q1_forecast: number
  fy_forecast: number
  key_assumptions: string[]
  risk_factors: string[]
}

interface Statistics {
  current_forecast: number
  forecast_accuracy: number
  variance_to_plan: number
  models_in_use: number
  scenarios_analyzed: number
  next_update: string
}

export default function RevenueForecastingPage() {
  const [forecasts, setForecasts] = useState<Forecast[]>([])
  const [models, setModels] = useState<ForecastModel[]>([])
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [forecastsRes, modelsRes, scenariosRes, statsRes] = await Promise.all([
        fetch('/api/revenueforecasting/forecasts'),
        fetch('/api/revenueforecasting/models'),
        fetch('/api/revenueforecasting/scenarios'),
        fetch('/api/revenueforecasting/statistics')
      ])
      if (forecastsRes.ok) setForecasts(await forecastsRes.json())
      if (modelsRes.ok) setModels(await modelsRes.json())
      if (scenariosRes.ok) setScenarios(await scenariosRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching revenue forecasting data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Revenue Forecasting</h1>
          <p className="mt-2 text-gray-600">Phase 240 - Forecasts, models, and scenarios</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Current Forecast</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.current_forecast)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Accuracy</p>
              <p className="text-2xl font-bold text-green-600">{statistics.forecast_accuracy}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Variance</p>
              <p className={`text-2xl font-bold ${statistics.variance_to_plan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {statistics.variance_to_plan > 0 ? '+' : ''}{statistics.variance_to_plan}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Models</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.models_in_use}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Scenarios</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.scenarios_analyzed}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Next Update</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.next_update}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Revenue Forecasts</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Baseline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Optimistic</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conservative</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">AI Prediction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">vs Target</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {forecasts.map((forecast) => (
                  <tr key={forecast.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{forecast.period}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(forecast.baseline)}</td>
                    <td className="px-6 py-4 text-green-600">{formatCurrency(forecast.optimistic)}</td>
                    <td className="px-6 py-4 text-red-600">{formatCurrency(forecast.conservative)}</td>
                    <td className="px-6 py-4 text-purple-600 font-bold">{formatCurrency(forecast.ai_prediction)}</td>
                    <td className="px-6 py-4 text-blue-600">{forecast.confidence}%</td>
                    <td className={`px-6 py-4 ${forecast.variance_to_target >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {forecast.variance_to_target > 0 ? '+' : ''}{forecast.variance_to_target}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Forecast Models</h2>
            </div>
            <div className="p-6 space-y-4">
              {models.map((model) => (
                <div key={model.id} className={`p-4 rounded-lg ${model.recommended ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{model.name}</span>
                    {model.recommended && <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Recommended</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Accuracy</p>
                      <p className="font-medium text-green-600">{model.accuracy}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">MAPE</p>
                      <p className="font-medium text-blue-600">{model.mape}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Bias</p>
                      <p className={`font-medium ${model.bias >= 0 ? 'text-green-600' : 'text-red-600'}`}>{model.bias}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Scenarios</h2>
            </div>
            <div className="p-6 space-y-4">
              {scenarios.map((scenario) => (
                <div key={scenario.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{scenario.name}</span>
                    <span className="text-sm text-gray-500">{scenario.probability}% probability</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Q1: {formatCurrency(scenario.q1_forecast)}</span>
                    <span className="text-gray-500">FY: {formatCurrency(scenario.fy_forecast)}</span>
                    <span className={`font-medium ${scenario.revenue_impact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {scenario.revenue_impact > 0 ? '+' : ''}{scenario.revenue_impact}%
                    </span>
                  </div>
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Assumptions:</p>
                    <div className="flex flex-wrap gap-1">
                      {scenario.key_assumptions.map((assumption, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{assumption}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Risks:</p>
                    <div className="flex flex-wrap gap-1">
                      {scenario.risk_factors.map((risk, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">{risk}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
